<template>
  <div>
    <section>
      <section
        v-if="!getAzureAccounts.length"
        class="tw-mt-6"
        style="text-align: center"
      >
        <div
          class="tw-bg-white tw-text-primary-grey-800 tw-w-full tw-min-h-[50dvh] tw-flex tw-items-center tw-justify-center tw-p-4 tw-border tw-border-primary-grey-100 tw-rounded-md tw-shadow-md tw-gap-4 tw-flex-col"
        >
          <div class="tw-text-xl">
            {{ t("email-security-no-connection") }}
          </div>
          <cl-icon-empty-email />

          <authorisation-wrapper
            :resource="ADMINISTRATORS"
            :action="WRITE"
            v-slot="{ isAllowed: isWriteAllowed }"
          >
            <cl-button
              type="submit"
              variant="secondary"
              :data-test-id="'settings-connection-add'"
              @on-click="onSubmit"
              :readonly="!isWriteAllowed"
            >
              {{ t("add-connection") }}
            </cl-button>
          </authorisation-wrapper>
        </div>
      </section>
    </section>

    <div v-if="getAzureAccounts.length">
      <div>
        <ConnectionsSteps v-if="isPhishServiceActive" />
        <div
          v-else
          class="tw-bg-white tw-text-primary-grey-800 tw-w-full tw-min-h-[50dvh] tw-flex tw-items-center tw-justify-center tw-p-4 tw-border tw-border-primary-grey-100 tw-rounded-md tw-shadow-md tw-gap-4 tw-flex-col"
        >
          <cl-icon-empty-dashboard class="tw-w-96" />
          <div class="tw-text-xl">{{ t("connected-to-m365") }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useAuthenticationStore } from "@/store/authentication.store";
import { storeToRefs } from "pinia";
import { useI18n } from "vue-i18n";
import { useSolutionsStore } from "@/store/solutions.store";
import { useAzureStore } from "@/store/azure.store";
import { usePageMetaStore } from "@/store/pageMeta.store";
import { useToastStore } from "@/components/TToasts/toast.store";
import ConnectionsSteps from "./components/ConnectionsSteps.vue";
import { PHISH_SERVICE } from "@/utils/services";
import { EXTERNAL_DOCS_LINKS } from "@/constants/externalDocsLinks";
import { AuthorisationWrapper, ADMINISTRATORS, WRITE } from "@/authorisation";
import { getCallbackUrl } from "@/config/runtimeConfig";
import { M365_CONNECTIONS_CLIENT_ID } from "@/constants/legacyClientId";
import { resolveCustomerOrgUuid } from "@/utils/navigation/customerOrgUuid";
import { AZURE_CALLBACK_PATH, buildAppReturnUrl } from "@/utils/returnUrl";
import { isEmbedded } from "@/remote/contract";
import { openConsentPopup, waitForConsentPopup } from "@/remote/consentPopup";

const { t } = useI18n();

const route = useRoute();
// The consent popup must come back to the UI that opened it (same origin and
// router base), so the state tells the API where this UI lives.
const router = useRouter();
const authStore = useAuthenticationStore();
const pageMetaStore = usePageMetaStore();
const toastStore = useToastStore();
const azureStore = useAzureStore();
const { azureAccounts: getAzureAccounts } = storeToRefs(azureStore);
const selectedCustomer = computed(() => authStore.selectedCustomer);
const selectedAccount = computed(() => authStore.selectedAccount);
const { solutions } = storeToRefs(useSolutionsStore());

const showConnection = ref(false);
const fetching = ref(false);
const callback = getCallbackUrl();
const receivedMessage = ref();
const timestamp = ref<string | null>(null);

const isPhishServiceActive = computed(() =>
  solutions.value.find(
    (s: { solution_uuid: string }) => s.solution_uuid === PHISH_SERVICE,
  ),
);

const channel = new BroadcastChannel("azure-connection");

watch(
  () => receivedMessage.value,
  async (newValue) => {
    const uuid = newValue;
    if (uuid && uuid.value?.data != null) {
      await azureStore.getAllAzureAccounts(uuid);
    }
  },
);

onMounted(async () => {
  try {
    fetching.value = true;
    const uuid = selectedCustomer.value?.uuid || selectedAccount.value?.uuid;
    const now = new Date();
    timestamp.value = now.toISOString().replace("T", " ").split(".")[0];
    await azureStore.getAllAzureAccounts(uuid);
  } catch (e) {
    /* eslint-disable no-console */
    console.log(e);
  } finally {
    fetching.value = false;
  }

  channel.onmessage = (event) => {
    receivedMessage.value = event.data;
  };

  pageMetaStore.setPageTitle({
    title: "Connections",
    info: {
      docsLink: EXTERNAL_DOCS_LINKS.CUSTOMER_CONNECTIONS_LEARN_MORE,
      text: t("connection-page-tooltip"),
    },
  });
});

onBeforeUnmount(() => {
  channel.close();
});

const onSubmit = () => {
  fetching.value = true;

  // state.o binds the customer's M365 tenant to an org, so it MUST be the
  // customer whose page this is — taken from the /customer/:orgUuid route,
  // never from selectedAccount (the logged-in System/MSP principal). Falling
  // back to the principal's org onboarded customers under the wrong org.
  const uuid = resolveCustomerOrgUuid(
    route.params.orgUuid,
    selectedCustomer.value?.uuid,
  );
  if (!uuid) {
    fetching.value = false;
    toastStore.displayToast({
      title: t("error"),
      message: t("generic-error"),
      duration: 5000,
      variant: "danger",
      testId: "settings-connection-toast-error",
    });
    return;
  }

  // Cosmetic label only; sourced from the customer, never the principal.
  const name = btoa(selectedCustomer.value?.name || "");
  const billingId = selectedCustomer.value?.billing_system_id;

  const state = {
    v: billingId,
    o: uuid,
    n: name,
    c: M365_CONNECTIONS_CLIENT_ID,
    t: timestamp.value,
    // Where the API sends the popup after consent; it must be on an origin the
    // API allows, otherwise the API falls back to its default frontend.
    r: buildAppReturnUrl(router, AZURE_CALLBACK_PATH),
  };

  const encodeObj = encodeURI(JSON.stringify(state));
  const strWindowFeatures =
    "location=yes,height=570,width=520,scrollbars=yes,status=yes";

  const URL = `https://login.microsoftonline.com/organizations/v2.0/adminconsent?client_id=${M365_CONNECTIONS_CLIENT_ID}&redirect_uri=${callback}&state=${encodeObj}&scope=https://outlook.office365.com/.default`;

  if (isEmbedded()) {
    // EMBEDDED: the red-app flow (remote/consentPopup.ts). The host announces
    // where the tagged popup came back and the wait closes it; then refresh
    // the accounts exactly as the standalone path does once its popup closes.
    const popup = openConsentPopup({ placeholder: t("loading") });
    if (!popup) {
      fetching.value = false;
      toastStore.displayToast({
        title: t("error"),
        message: t("generic-error"),
        duration: 5000,
        variant: "danger",
        testId: "settings-connection-toast-error",
      });
      return;
    }
    popup.window.location.href = URL;
    void waitForConsentPopup(popup)
      .catch((error: unknown) => {
        console.error(error);
      })
      .then(async () => {
        fetching.value = false;
        showConnection.value = false;
        await azureStore.getAllAzureAccounts(uuid);
      });
    return;
  }

  const popupWindow = window.open(URL, "_blank", strWindowFeatures);

  const checkIfPopupWindowClosed = setInterval(async () => {
    if (popupWindow?.closed) {
      clearInterval(checkIfPopupWindowClosed);
      fetching.value = false;
      showConnection.value = false;
      await azureStore.getAllAzureAccounts(uuid);
    }
  }, 500);
};
</script>
